import React, { useEffect, useRef, useState } from 'react';

import {
  Popover,
  PopoverArrow,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  useOutsideClick,
} from '@chakra-ui/react';

import { useTranslation } from 'react-i18next';

import '../../../locales/i18n';

import {
  Box,
  Button,
  Flex,
  Heading,
  Input,
  Loading,
  Text,
} from '@brand/components';
import { Search2Icon } from '@chakra-ui/icons';
import helpers from 'helpers';

import { useReduxDispatch } from 'hooks/redux/useReduxDispatch';
import { useReduxSelector } from 'hooks/redux/useReduxSelector';

import ComponentIsVisible from 'components/utils/IsVisible';

import authSelectors from 'store/slices/auth/selectors';
import { interventionActions } from 'store/slices/intervention';
import interventionSelectors from 'store/slices/intervention/selectors';

import SearchPopOverContent from './SearchPopOverContent';

const MIN_CHARACTERS_SEARCH = 3;

const SearchBar = () => {
  const { t } = useTranslation('navigation');
  const reduxDispatch = useReduxDispatch();
  const popOverRef = useRef<any>();
  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);

  const [inputValue, setInputValue] = useState<string>('');

  const [timer, setTimer] = useState<any>();

  const [timerHelper, setTimerHelper] = useState<number>();
  const [showMinCharHelper, setShowMinCharHelper] = useState<boolean>(false);

  const interventionSearchList = useReduxSelector(
    interventionSelectors.getSearchedInterventionList,
  );
  const interventionSearchLoading = useReduxSelector(
    interventionSelectors.getSearchedInterventionIsLoading,
  );
  const interventionSearchEmptyMsg = useReduxSelector(
    interventionSelectors.getSearchedInterventionEmptyMsg,
  );

  const traderProfile = useReduxSelector(authSelectors.profile);
  const handleSearchDetails = () => {
    if (inputValue.length < MIN_CHARACTERS_SEARCH) return;
    if (!traderProfile.intervention_partnership_id) return;

    reduxDispatch(
      interventionActions.getSearchedInterventionRequest({
        data: {
          interventionPartnershipId: traderProfile.intervention_partnership_id,
          request: inputValue && inputValue.replace(/\s/g, ''),
        },
        functions: {
          error: (err: any) => {
            helpers.errorHandling(err);
          },
        },
      }),
    );
  };

  useEffect(() => {
    clearTimeout(timer);
    clearTimeout(timerHelper);
    setShowMinCharHelper(false);

    setTimer(
      setTimeout(() => {
        if (inputValue) {
          handleSearchDetails();
        }
      }, 700),
    );

    setTimerHelper(
      window.setTimeout(() => {
        if (inputValue && inputValue.length < MIN_CHARACTERS_SEARCH) {
          setShowMinCharHelper(true);
        }
      }, 2500),
    );

    return () => {
      clearTimeout(timer);
      clearTimeout(timerHelper);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    if (inputValue.length >= MIN_CHARACTERS_SEARCH || showMinCharHelper) {
      setIsPopoverOpen(true);
    }
    return () => {
      setIsPopoverOpen(false);
    };
  }, [showMinCharHelper, inputValue]);

  useOutsideClick({
    ref: popOverRef,
    handler: () => setIsPopoverOpen(false),
  });

  const handleInputSearchValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };
  return (
    <Box ref={popOverRef} sx={{ zIndex: '99' }}>
      <Popover
        autoFocus={false}
        closeOnBlur={false}
        isOpen={isPopoverOpen}
        placement="bottom"
        returnFocusOnClose={false}
      >
        <PopoverTrigger>
          <Flex>
            <Input
              backgroundColor="white"
              color="secondary"
              onChange={handleInputSearchValue}
              placeholder={t('navigation:navbar.search.placeholder')}
              value={inputValue}
            />
            <ComponentIsVisible when={interventionSearchLoading}>
              <Button padding="12px 16px" size="xs">
                <Loading size={3} thickness={12} />
              </Button>
            </ComponentIsVisible>
            <ComponentIsVisible when={!interventionSearchLoading}>
              <Button
                onClick={() => setIsPopoverOpen(!isPopoverOpen)}
                padding="12px 16px"
                size="xs"
              >
                <Search2Icon />
              </Button>
            </ComponentIsVisible>
          </Flex>
        </PopoverTrigger>
        <ComponentIsVisible
          when={
            inputValue.length >= MIN_CHARACTERS_SEARCH &&
            !interventionSearchLoading &&
            (interventionSearchList.length > 0 || !!interventionSearchEmptyMsg)
          }
        >
          <PopoverContent width={[`350px`, `400px`, `500px`]}>
            <PopoverHeader fontWeight="semibold">
              <Flex
                align="center"
                direction="column"
                justify="center"
                sx={{ marginTop: '15px' }}
              >
                <ComponentIsVisible when={showMinCharHelper}>
                  <Text p={2} size="sm" textAlign="center">
                    {t('navigation:navbar.search.minimumCharactersText')}
                  </Text>
                </ComponentIsVisible>
                <ComponentIsVisible when={interventionSearchList.length > 0}>
                  <Heading textAlign="center" variant="h6">
                    {`${t('navigation:navbar.search.matchingInterventions')}
                  ${interventionSearchList.length}`}
                  </Heading>
                </ComponentIsVisible>
                <ComponentIsVisible
                  when={
                    inputValue.length >= MIN_CHARACTERS_SEARCH &&
                    interventionSearchList.length === 0 &&
                    !!interventionSearchEmptyMsg
                  }
                >
                  <Heading textAlign="center" variant="h6">
                    {t('navigation:navbar.search.noMatchingInterventions')}
                  </Heading>
                </ComponentIsVisible>
              </Flex>
            </PopoverHeader>
            <PopoverArrow />
            <PopoverCloseButton
              color="black"
              onClick={() => setIsPopoverOpen(false)}
            />
            <ComponentIsVisible when={interventionSearchList.length > 0}>
              <SearchPopOverContent
                interventionSearchList={interventionSearchList}
              />
            </ComponentIsVisible>
          </PopoverContent>
        </ComponentIsVisible>
      </Popover>
    </Box>
  );
};

export default SearchBar;
